/**
 * Delete a declaration, after saying what that does.
 *
 * Issue #80. Deleting an element removes it, what is written inside it, and the
 * links to and from it - never another element because that element was linked
 * to it. What is only LINKED is unlinked and stays; what cannot exist without the
 * deleted element (a connection typed by a deleted interface, a definition that
 * specializes a deleted one, ...) goes with it, and is listed in a section of its
 * own that is hard to miss, because it is the part that tells someone they are
 * deleting more than they clicked on. There is no cascade to offer and one
 * confirm: the dialog asks the API what the delete WOULD do (a read-only route,
 * answered by the same plan the delete then carries out) and spells it out
 * before anything changes.
 *
 * What it says is the model's own: a requirement's dialog counts the
 * requirements that will lose it as a source and names the ones left with none;
 * a part's dialog lists the usages that go and says the definitions they are
 * instances of stay. The tree only holds what the listing returned, so counting
 * from it would miss a requirement in a file that was filtered out.
 *
 * The Delete button waits for the answer. A person who confirms without having
 * been told what happens has not been told.
 */

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { ApiError, api } from '../../api'
import type { DeleteEffect, DeleteImpact, ElementRow } from '../../schema'
import { MONOSPACE } from '../../theme'
import { confirmLabel, dependentsHeading, kindWord, plural, summarize } from './deleteSummary'

/** What the dialog needs of a row, so a tree row and a detail page can both open it. */
export type DeleteTarget = Pick<ElementRow, 'plural' | 'id' | 'name'>

export default function DeleteElementDialog({
  row,
  title,
  onClose,
  onDeleted,
}: {
  row: DeleteTarget
  /** The question asked, when "Delete X?" is not how the caller puts it. */
  title?: string
  onClose: () => void
  onDeleted: (message: string) => void
}) {
  const [impact, setImpact] = useState<DeleteImpact | null>(null)
  const [loadFailure, setLoadFailure] = useState('')
  const [busy, setBusy] = useState(false)
  const [refusal, setRefusal] = useState('')

  const load = useCallback(() => {
    setImpact(null)
    setLoadFailure('')
    api
      .deleteImpact(String(row.plural), row.id)
      .then(setImpact)
      .catch((error: ApiError) => setLoadFailure(error.message))
  }, [row.plural, row.id])

  useEffect(() => {
    load()
  }, [load])

  const remove = async () => {
    setBusy(true)
    setRefusal('')
    try {
      const result = await api.remove(String(row.plural), row.id)
      const alongside = result.removed.length - 1
      const unlinked = result.unlinked.length
      const detail = [
        alongside > 0 ? `${alongside} written in it or instantiating it removed` : '',
        unlinked > 0 ? plural(unlinked, 'link') + ' removed' : '',
      ].filter(Boolean)
      onDeleted(`${result.identifier} deleted${detail.length ? ` (${detail.join(', ')})` : ''}`)
    } catch (error) {
      // What is left to refuse is not about children: a read-only branch, or a
      // model that would no longer regenerate. Shown as written; a second click
      // changes nothing about either.
      setRefusal((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <WarningAmberIcon color="warning" />
          <span>{title ?? `Delete ${row.id || row.name}?`}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            The <code>.sysml</code> is regenerated without it. The file in the repository is the
            record, so an unwanted deletion is undone by not committing it. Elements that are only
            linked to this one are unlinked, not deleted. Anything that cannot exist without it is
            removed with it, and is listed below.
          </DialogContentText>

          {!impact && !loadFailure && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Checking what this affects…</Typography>
            </Stack>
          )}
          {loadFailure && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={load}>
                  Try again
                </Button>
              }
            >
              Could not check what this affects: {loadFailure}
            </Alert>
          )}
          {impact && <Consequences impact={impact} />}
          {refusal && <Alert severity="error">{refusal}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => void remove()}
          disabled={busy || !impact}
        >
          {busy ? 'Deleting…' : confirmLabel(impact?.removes.length ?? 1)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** The plan, read out: what goes, what is only unlinked, what is left alone. */
function Consequences({ impact }: { impact: DeleteImpact }) {
  const summary = summarize(impact)
  return (
    <Stack spacing={1.5}>
      <Section title="Removed">
        <Typography variant="body2">{summary.headline}</Typography>
        {summary.alongside.map((line) => (
          <Typography key={line} variant="body2">
            {line}
          </Typography>
        ))}
      </Section>

      {summary.dependents.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" gutterBottom>
            {dependentsHeading(impact.identifier)}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {summary.dependents.map((entry) => (
              <li key={`${entry.kind}:${entry.identifier}`}>
                <Typography variant="body2" component="span">
                  {kindWord(entry.kind)}{' '}
                  <span style={{ fontFamily: MONOSPACE }}>{entry.identifier}</span>
                  {entry.because ? ` - ${entry.because}` : ''}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {summary.sources.length > 0 && (
        <Section title="Unlinked, not deleted">
          {summary.sources.map((line) => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
        </Section>
      )}

      {summary.unlinks.length > 0 && (
        <Section title={summary.sources.length ? 'Also unlinked' : 'Unlinked, not deleted'}>
          <Effects effects={summary.unlinks} />
        </Section>
      )}

      {summary.kept.length > 0 && (
        <Section title="Left exactly as written">
          <Typography variant="body2" color="text.secondary">
            These need the reference, so nothing is invented for them. They stay as they are,
            and BEST will report each one as a finding.
          </Typography>
          <Effects effects={summary.kept} />
        </Section>
      )}
    </Stack>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Stack spacing={0.5}>{children}</Stack>
    </Box>
  )
}

/** One line per other element, its ID first so a long list can be scanned. */
function Effects({ effects }: { effects: DeleteEffect[] }) {
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {effects.map((effect) => (
        <li key={`${effect.relation}:${effect.identifier}:${effect.detail}`}>
          <Typography variant="body2" component="span">
            <span style={{ fontFamily: MONOSPACE }}>{effect.identifier}</span> {effect.detail}
          </Typography>
        </li>
      ))}
    </Box>
  )
}
